/**
 * Is this video the song? Show a link's REAL title before anything is saved
 * from it, and say so when the title does not name the song.
 *
 * WHY (Anthony, 2026-10-22). Addison Swenson's "Placido zeffiretto" carried a
 * lyrics video that was a different song by the same singer ("Vorrei di te
 * fidarmi") and a karaoke video of "On My Own", and a track was cut from the
 * wrong one. Every step had shown a label ("Lyrics video", a pasted URL), never
 * the video's own title, so nobody could see it was wrong until a student
 * would have heard it.
 *
 * The title comes from YouTube's oEmbed endpoint: no API key, no quota, and it
 * answers this site's origin with CORS (checked 2026-10-22). It is a WARNING,
 * never a block: a classical aria is often titled by its opera, a cover by its
 * artist, and the teacher is the judge.
 */

// Words that carry no identity in a song title or a video title.
const STOPWORDS = new Set([
	'the', 'and', 'from', 'feat', 'featuring', 'with', 'for', 'version', 'karaoke',
	'lyrics', 'lyric', 'video', 'official', 'audio', 'instrumental', 'original',
	'broadway', 'cast', 'recording', 'soundtrack', 'live', 'cover', 'backing', 'track',
]);

const words = (text) =>
	String(text || '')
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.split(' ')
		.filter((w) => w.length >= 3 && !STOPWORDS.has(w));

/**
 * true when the title names the song (at least half its meaningful words),
 * false when it does not, null when the song name has nothing to match on
 * ("L-O-V-E", "Me") — no warning beats a wrong one.
 */
export const titleMatchesSong = (title, songName) => {
	// A parenthetical is usually "(from Brooklyn)" or "(feat. X)": not the song.
	const songWords = [...new Set(words(String(songName || '').replace(/[([].*?[)\]]/g, ' ')))];
	if (songWords.length === 0 || !title) return null;
	const titleWords = new Set(words(title));
	const hits = songWords.filter((w) => titleWords.has(w)).length;
	return hits >= Math.ceil(songWords.length / 2);
};

const YOUTUBE_LINK = /^https?:\/\/(www\.|m\.|music\.)?(youtube\.com|youtu\.be)\/\S+$/i;

/** { title, channel } for a YouTube link, or null when it cannot be read. */
export const fetchYoutubeTitle = async (url, fetchImpl = typeof fetch === 'function' ? fetch : null) => {
	const link = String(url || '').trim();
	if (!YOUTUBE_LINK.test(link) || !fetchImpl) return null;
	try {
		const res = await fetchImpl(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(link)}`);
		if (!res.ok) return null;
		const json = await res.json();
		return json && json.title ? { title: String(json.title), channel: String(json.author_name || '') } : null;
	} catch {
		return null;
	}
};
